import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { GraduationCap, Copy, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { LogoDark } from "../components/Logo";
import { LanguageSwitcher } from "../components/LanguageSwitcher";

const inp = "w-full rounded-xl border border-slate-200 bg-white py-2.5 px-4 text-sm text-slate-900 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100";
const lbl = "mb-1 block text-sm font-medium text-slate-700";

const GRADES = ["5", "6", "7", "8", "9", "10", "11", "12"];
const DIVISIONS = ["A", "B", "C", "D", "E", "F"];

export default function Enroll() {
  const { t } = useTranslation();
  const { user, refreshUser } = useAuth();
  const [form, setForm] = useState({
    full_name: user?.full_name || "",
    dob_date: "",
    grade: "8",
    division: "A",
    school_name: "",
    city: "",
    state: "",
    parent_mobile: "",
  });
  const [saving, setSaving] = useState(false);
  const [creds, setCreds] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const [, mm, dd] = form.dob_date.split("-"); // yyyy-mm-dd
    if (!dd || !mm) { toast.error(t("enroll.dob_required")); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/auth/complete-profile", {
        full_name: form.full_name,
        date_of_birth: `${dd}${mm}`,
        grade: form.grade,
        division: form.division,
        school_name: form.school_name,
        city: form.city,
        state: form.state,
        parent_mobile: form.parent_mobile,
      });
      localStorage.setItem("gd_user", JSON.stringify(data.user));
      setCreds(data.credentials);
      toast.success(t("enroll.success"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("enroll.failed"));
    } finally {
      setSaving(false);
    }
  };

  const copyCreds = () => {
    navigator.clipboard.writeText(
      `Student: ${creds.username} / ${creds.password}\nParent: ${creds.parent_username} / ${creds.parent_password}`
    );
    toast.success(t("enroll.copied"));
  };

  return (
    <div className="relative min-h-screen bg-gradient-to-br from-violet-50 via-slate-50 to-indigo-50 px-4 py-10">
      <div className="absolute right-4 top-4 z-10"><LanguageSwitcher variant="light" /></div>
      <div className="pointer-events-none absolute -top-32 -left-32 h-96 w-96 rounded-full bg-violet-200/40 blur-3xl" />
      <div className="relative mx-auto w-full max-w-lg">
        <div className="flex justify-center pb-6"><LogoDark size={44} /></div>
        <div className="rounded-3xl bg-white/90 p-8 shadow-xl shadow-slate-900/5 backdrop-blur-sm" data-testid="enroll-card">
          {!creds ? (
            <>
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-indigo-100 text-indigo-600"><GraduationCap size={22} /></div>
                <div>
                  <h1 className="text-xl font-bold text-slate-900">{t("enroll.title")}</h1>
                  <p className="text-sm text-slate-500">{t("enroll.subtitle")}</p>
                </div>
              </div>
              <form onSubmit={submit} className="mt-6 space-y-4">
                <div>
                  <label className={lbl}>{t("enroll.full_name")}</label>
                  <input required data-testid="enroll-name" value={form.full_name} onChange={set("full_name")} className={inp} />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className={lbl}>{t("enroll.dob")}</label>
                    <input required type="date" data-testid="enroll-dob" value={form.dob_date} onChange={set("dob_date")} className={inp} />
                  </div>
                  <div>
                    <label className={lbl}>{t("enroll.grade")}</label>
                    <select data-testid="enroll-grade" value={form.grade} onChange={set("grade")} className={inp}>
                      {GRADES.map((g) => <option key={g} value={g}>{g}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className={lbl}>{t("enroll.division")}</label>
                    <select data-testid="enroll-division" value={form.division} onChange={set("division")} className={inp}>
                      {DIVISIONS.map((d) => <option key={d} value={d}>{d}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label className={lbl}>{t("enroll.school_name")}</label>
                  <input required data-testid="enroll-school" value={form.school_name} onChange={set("school_name")} placeholder={t("enroll.school_ph")} className={inp} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={lbl}>{t("enroll.city")}</label>
                    <input required data-testid="enroll-city" value={form.city} onChange={set("city")} className={inp} />
                  </div>
                  <div>
                    <label className={lbl}>{t("enroll.state")}</label>
                    <input required data-testid="enroll-state" value={form.state} onChange={set("state")} className={inp} />
                  </div>
                </div>
                <div>
                  <label className={lbl}>{t("enroll.parent_mobile")}</label>
                  <input required type="tel" data-testid="enroll-parent-mobile" value={form.parent_mobile} onChange={set("parent_mobile")} placeholder="+91 98765 43210" className={inp} />
                </div>
                <button type="submit" disabled={saving} data-testid="enroll-submit"
                  className="w-full rounded-xl bg-indigo-600 py-3 text-base font-semibold text-white shadow-lg shadow-indigo-600/20 transition hover:bg-indigo-700 disabled:opacity-60">
                  {saving ? t("enroll.creating") : t("enroll.submit")}
                </button>
              </form>
            </>
          ) : (
            <div data-testid="enroll-credentials">
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-100 text-emerald-600"><CheckCircle2 size={22} /></div>
                <div>
                  <h1 className="text-xl font-bold text-slate-900">{t("enroll.creds_title")}</h1>
                  <p className="text-sm text-slate-500">{t("enroll.creds_note")}</p>
                </div>
              </div>
              <div className="mt-6 space-y-3">
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <p className="text-xs font-bold uppercase tracking-wide text-slate-500">{t("enroll.student_login")}</p>
                  <p className="mt-1 font-mono text-sm text-slate-800" data-testid="enroll-student-creds">{creds.username} / {creds.password}</p>
                </div>
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <p className="text-xs font-bold uppercase tracking-wide text-slate-500">{t("enroll.parent_login")}</p>
                  <p className="mt-1 font-mono text-sm text-slate-800" data-testid="enroll-parent-creds">{creds.parent_username} / {creds.parent_password}</p>
                </div>
                <button onClick={copyCreds} data-testid="enroll-copy-creds"
                  className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50">
                  <Copy size={14} /> {t("enroll.copy")}
                </button>
                <button
                  data-testid="enroll-continue"
                  onClick={async () => { try { await refreshUser(); } catch {} window.location.replace("/student/dashboard"); }}
                  className="w-full rounded-xl bg-indigo-600 py-3 text-base font-semibold text-white shadow-lg shadow-indigo-600/20 transition hover:bg-indigo-700">
                  {t("enroll.continue")}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
